import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { combineLatest, forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, scan, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { chunk, flatten } from 'lodash-es';

import { BaseContainer } from '@stylehaven/components';
import { ApiFragment } from '../../../record/entities/api-fragment.entity';
import { PrintLabelRequest, RecordWithQRCode } from '../../entities/print.entity';
import { LabelPrintSandboxService } from '../../sandbox/label-print-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-label-print-overview-container',
    templateUrl: './label-print-overview-container.component.html',
    styleUrls: ['./label-print-overview-container.component.scss']
})
export class LabelPrintOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    readonly records$: Subject<PrintLabelRequest[]> = new ReplaySubject<PrintLabelRequest[]>(1);

    startLoad$: Subject<void> = new Subject<void>();
    imageLoaded$: Subject<RecordWithQRCode> = new Subject<RecordWithQRCode>();

    loadedImages$!: Observable<number>;

    images$!: Observable<RecordWithQRCode[][]>;

    @ViewChild('printTemplate', { read: ElementRef })
    private printTemplate!: ElementRef<HTMLDivElement>;

    @Input()
    set records(records: (ApiFragment | PrintLabelRequest)[]) {
        this.records$.next(records.map(record => (record instanceof ApiFragment ? { record } : record)));
    }

    @Output() printClose: EventEmitter<void> = new EventEmitter<void>();

    constructor(
        private readonly sandbox: LabelPrintSandboxService,
        private readonly sanitizer: DomSanitizer
    ) {
        super();
    }

    ngOnInit(): void {
        this.initStateStreams();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.startLoad$.complete();
        this.imageLoaded$.complete();
    }

    imageLoaded(image: RecordWithQRCode): void {
        this.imageLoaded$.next(image);
    }

    private initStateStreams(): void {
        this.loadedImages$ = this.startLoad$.pipe(switchMap(() => this.imageLoaded$.pipe(scan(acc => acc + 1, 0))));
        this.images$ = this.records$.pipe(
            tap(() => this.startLoad$.next()),
            switchMap((request: PrintLabelRequest[]) =>
                forkJoin(request.flatMap(record => this.mapRecordToRecordWithQRCode(record)))
            ),
            map(items => chunk(items, 16)),
            takeUntil(this.destroy$)
        );

        combineLatest([this.images$, this.loadedImages$])
            .pipe(
                filter(([images, loaded]) => flatten(images).length === loaded),
                switchMap(() => this.sandbox.print(this.printTemplate.nativeElement)),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.printClose.next());
    }

    private mapRecordToRecordWithQRCode(record: PrintLabelRequest): Observable<RecordWithQRCode>[] {
        if (record.amount === undefined) {
            return [this.generateQrCode(record)];
        }

        return Array.from({ length: record.amount }, (_, i) => i + 1).map(index => this.generateQrCode(record, index));
    }

    private generateQrCode(record: PrintLabelRequest, index?: number): Observable<RecordWithQRCode> {
        return this.sandbox.qr(record.record.getRecordId(), index !== undefined ? { index } : {}).pipe(
            map(qr => URL.createObjectURL(qr)),
            map(objectUrl => this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl)),
            map(qrCode => ({
                record: record.record,
                qrCode,
                ...(index !== undefined ? { index, total: record.amount } : {})
            })),
            take(1)
        );
    }
}
